import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  getClickPoint,
  getDefaultCriteriaNote,
  getElectricalClickPoints,
  getElectricalCriteriaNote,
  getElectricalMeasuredBy,
  getElectricalMeasurementConditions,
  getElectricalOperatingVoltage,
  getElectricalSignatureText,
  getElectricalTemperatureDiff,
  getInspectorDateParts,
  toText,
} from "components/project/electricalSafetyReportUtils";
import { streamUrlToBase64 } from "lib/utils/streamUrlToBase64";
import {
  getUserSignatureProfile,
  SIGNATURE_IMAGE_MAX_SIZE,
} from "lib/utils/userSignatureProfile";

const TEMPLATE_URL = `${
  process.env.PUBLIC_URL || ""
}/templates/electrical-safety-template.xlsx`;
const SIGNATURE_IMAGE_LEFT_PADDING_PX = 6;

function setCellValue(sheet, address, value) {
  sheet.getCell(address).value = toText(value);
}

function cloneStyle(style) {
  if (!style) return {};
  return JSON.parse(JSON.stringify(style));
}

function setInspectorLine(sheet, file, user) {
  const measuredBy = getElectricalMeasuredBy(file, user);
  const signatureText = getElectricalSignatureText(file);
  const inspectorDate = getInspectorDateParts(file);
  const dateText = [inspectorDate.year, inspectorDate.month, inspectorDate.day]
    .filter(Boolean)
    .map((value, index) => {
      if (index === 0) return `${value}년`;
      if (index === 1) return `${value}월`;
      return `${value}일`;
    })
    .join(" ");

  const baseStyle = cloneStyle(sheet.getCell("A4").style);
  const sections = [
    {
      range: "A4:C4",
      anchor: "A4",
      value: measuredBy ? `측정자 : ${measuredBy}` : "측정자 :",
      horizontal: "left",
    },
    {
      range: "D4:F4",
      anchor: "D4",
      value: toText(signatureText),
      horizontal: "left",
    },
    {
      range: "G4:N4",
      anchor: "G4",
      value: dateText,
      horizontal: "right",
    },
  ];

  sheet.unMergeCells("A4:N4");

  sections.forEach(({ range, anchor, value, horizontal }) => {
    sheet.mergeCells(range);
    const cell = sheet.getCell(anchor);
    cell.style = cloneStyle(baseStyle);
    cell.value = value;
    cell.alignment = {
      ...(cell.alignment ?? {}),
      horizontal,
      vertical: "middle",
      wrapText: false,
    };
  });
}

function getBase64ImageExtension(base64) {
  const match = /^data:image\/([a-zA-Z0-9+.-]+);base64,/.exec(base64 ?? "");
  const extension = match?.[1]?.toLowerCase();

  if (extension === "jpg") return "jpeg";
  if (extension === "svg+xml") return "png";
  return extension || "png";
}

function addBase64Image(workbook, sheet, base64, range) {
  if (!base64 || typeof base64 !== "string") return;

  const imageId = workbook.addImage({
    base64,
    extension: getBase64ImageExtension(base64),
  });

  sheet.addImage(imageId, range);
}

function getColumnPixelWidth(sheet, columnNumber) {
  const width = sheet.getColumn(columnNumber).width;
  if (!Number.isFinite(width)) return 64;
  if (width <= 1) return Math.floor(width * 12 + 0.5);
  return Math.floor(width * 7 + 5);
}

function getRowPixelHeight(sheet, rowNumber) {
  const height = sheet.getRow(rowNumber).height;
  return Number.isFinite(height) ? Math.round((height * 96) / 72) : 20;
}

function getRangePixelSize(sheet, startCol, endCol, startRow, endRow) {
  let width = 0;
  let height = 0;

  for (let col = startCol; col <= endCol; col += 1) {
    width += getColumnPixelWidth(sheet, col);
  }

  for (let row = startRow; row <= endRow; row += 1) {
    height += getRowPixelHeight(sheet, row);
  }

  return {
    width: width + Math.max(endCol - startCol, 0),
    height,
  };
}

function pixelOffsetToColumnPosition(sheet, startCol, pixelOffset) {
  let remaining = Math.max(pixelOffset, 0);
  let columnPosition = startCol - 1;

  for (let col = startCol; ; col += 1) {
    const columnWidth = getColumnPixelWidth(sheet, col);
    if (remaining <= columnWidth) {
      return columnPosition + remaining / columnWidth;
    }

    remaining -= columnWidth;
    columnPosition += 1;
  }
}

function pixelOffsetToRowPosition(sheet, startRow, pixelOffset) {
  let remaining = Math.max(pixelOffset, 0);
  let rowPosition = startRow - 1;

  for (let row = startRow; ; row += 1) {
    const rowHeight = getRowPixelHeight(sheet, row);
    if (remaining <= rowHeight) {
      return rowPosition + remaining / rowHeight;
    }

    remaining -= rowHeight;
    rowPosition += 1;
  }
}

function fitImageSize(width, height, maxWidth, maxHeight) {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return {
      width: Math.max(Math.min(maxWidth, SIGNATURE_IMAGE_MAX_SIZE.maxWidth), 1),
      height: Math.max(Math.min(maxHeight, maxHeight), 1),
    };
  }

  const scale = Math.min(maxWidth / width, maxHeight / height, 1);
  return {
    width: Math.max(Math.round(width * scale), 1),
    height: Math.max(Math.round(height * scale), 1),
  };
}

async function getImageDimensions(base64) {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.onerror = reject;
    image.src = base64;
  });
}

async function addInspectorSignatureImage(workbook, sheet, user) {
  const signatureProfile = getUserSignatureProfile(user);
  if (!signatureProfile?.imageUrl) return;

  const base64 = await streamUrlToBase64(signatureProfile.imageUrl);
  const imageSize = await getImageDimensions(base64);
  const signatureRange = getRangePixelSize(sheet, 4, 6, 4, 4);
  const maxWidth = signatureRange.width;
  const maxHeight = signatureRange.height;
  const fittedSize = fitImageSize(
    imageSize.width,
    imageSize.height,
    maxWidth,
    maxHeight,
  );
  const leftOffset = SIGNATURE_IMAGE_LEFT_PADDING_PX;
  const topOffset = Math.max(signatureRange.height - fittedSize.height, 0);
  const imageId = workbook.addImage({
    base64,
    extension: getBase64ImageExtension(base64),
  });

  sheet.addImage(imageId, {
    tl: {
      col: pixelOffsetToColumnPosition(sheet, 4, leftOffset),
      row: pixelOffsetToRowPosition(sheet, 4, topOffset),
    },
    ext: {
      width: fittedSize.width,
      height: fittedSize.height,
    },
    editAs: "oneCell",
  });
}

export async function loadElectricalSafetyExcelTemplateWorkbook() {
  const response = await fetch(TEMPLATE_URL);

  if (!response.ok) {
    throw new Error(`template fetch failed: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  return workbook;
}

export async function populateElectricalSafetyExcelWorksheet({
  workbook,
  sheet,
  file,
  images,
  user,
}) {
  if (!workbook || !sheet) {
    throw new Error("populateElectricalSafetyExcelWorksheet requires workbook and sheet");
  }

  const clickPoints = getElectricalClickPoints(file);
  const criteriaNote = getElectricalCriteriaNote(file);
  const point1 = getClickPoint(clickPoints[0], "Point 1");
  const point2 = getClickPoint(clickPoints[1], "Point 2");
  const point3 = getClickPoint(clickPoints[2], "Point 3");

  setInspectorLine(sheet, file, user);
  try {
    await addInspectorSignatureImage(workbook, sheet, user);
  } catch (error) {
    console.warn(
      "[ElectricalSafetyExcelDownloadBtn] failed to embed signature image",
      error,
    );
  }
  setCellValue(sheet, "B5", file?.facility_name);
  setCellValue(sheet, "H5", getElectricalOperatingVoltage(file));
  setCellValue(sheet, "N5", getElectricalMeasurementConditions(file));

  if (criteriaNote) {
    setCellValue(sheet, "A9", criteriaNote);
  } else if (
    sheet.getCell("A9").value == null ||
    sheet.getCell("A9").value === ""
  ) {
    setCellValue(sheet, "A9", getDefaultCriteriaNote());
  }

  setCellValue(sheet, "D11", point1.label);
  setCellValue(sheet, "G11", point2.label);
  setCellValue(sheet, "J11", point3.label);

  setCellValue(sheet, "D12", point1.value);
  setCellValue(sheet, "G12", point2.value);
  setCellValue(sheet, "J12", point3.value);
  setCellValue(sheet, "M12", getElectricalTemperatureDiff(file));
  setCellValue(sheet, "A17", file?.inspection_result);

  sheet.getCell("A14").value = "";
  sheet.getCell("H14").value = "";

  addBase64Image(workbook, sheet, images?.image_visual, "A14:G14");
  addBase64Image(
    workbook,
    sheet,
    images?.markup_image_triplespot ?? images?.markup_image_thermal,
    "H14:N14",
  );
}

export async function downloadElectricalSafetyExcelReport({
  file,
  images,
  user,
}) {
  const workbook = await loadElectricalSafetyExcelTemplateWorkbook();
  const sheet = workbook.worksheets[0];

  await populateElectricalSafetyExcelWorksheet({
    workbook,
    sheet,
    file,
    images,
    user,
  });

  const fileName =
    file?.file_clean_file_name ||
    file?.clean_file_name ||
    file?.upload_id ||
    "electrical_safety_report";

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
}

export default function ElectricalSafetyExcelDownloadBtn({
  file,
  images,
  user,
  label = "Excel Download",
}) {
  const handleDownload = async () => {
    try {
      await downloadElectricalSafetyExcelReport({ file, images, user });
    } catch (error) {
      console.error(
        "[ElectricalSafetyExcelDownloadBtn] download failed",
        error,
      );
    }
  };

  return (
    <button className="btn-bg-blue" onClick={handleDownload}>
      {label}
    </button>
  );
}
